// Comprehensive Cigar Database for Ring & Ash
// Used for Today's Smoke, recommendations, and taste intelligence

export interface Cigar {
  id: string;
  brand: string;
  line: string;
  vitola?: string;
  wrapper: string;
  binder: string;
  filler: string[];
  origin: string;
  strength: 'mild' | 'mild-medium' | 'medium' | 'medium-full' | 'full';
  body: 'light' | 'medium' | 'full';
  flavorNotes: string[];
  primaryFlavors: string[]; // Top 3 dominant flavors
  timeCommitment: number; // Minutes
  priceRange: 'budget' | 'moderate' | 'premium' | 'luxury';
  pairings: string[];
  description: string;
  idealMoment?: string; // When to smoke this
  rating?: number; // 1-100 scale
}

export interface Pairing {
  id: string;
  cigarId: string;
  drink: string;
  drinkType: 'coffee' | 'whiskey' | 'rum' | 'bourbon' | 'scotch' | 'wine' | 'beer' | 'cognac' | 'non-alcoholic';
  description: string;
  whyItWorks: string;
}

export interface AshWisdom {
  id: string;
  text: string;
  author?: string;
  category: 'ritual' | 'presence' | 'craft' | 'reflection' | 'conversation';
}

// ============================================
// CIGAR DATABASE - 50+ Premium Cigars
// ============================================

export const CIGARS: Cigar[] = [
  // MILD CIGARS
  {
    id: 'arturo-fuente-chateau',
    brand: 'Arturo Fuente',
    line: 'Chateau Fuente',
    vitola: 'Rothschild',
    wrapper: 'Cameroon',
    binder: 'Dominican',
    filler: ['Dominican'],
    origin: 'Dominican Republic',
    strength: 'mild',
    body: 'light',
    flavorNotes: ['cedar', 'cream', 'nuts', 'subtle spice', 'honey'],
    primaryFlavors: ['cedar', 'cream', 'nuts'],
    timeCommitment: 45,
    priceRange: 'moderate',
    pairings: ['coffee', 'light rum', 'cream soda'],
    description: 'A Dominican classic with silky Cameroon wrapper. Perfect introduction to premium cigars.',
    idealMoment: 'Morning with coffee or afternoon relaxation',
  },
  {
    id: 'macanudo-cafe',
    brand: 'Macanudo',
    line: 'Café',
    vitola: 'Hampton Court',
    wrapper: 'Connecticut Shade',
    binder: 'Mexican',
    filler: ['Dominican', 'Mexican'],
    origin: 'Dominican Republic',
    strength: 'mild',
    body: 'light',
    flavorNotes: ['cream', 'cedar', 'almonds', 'light floral'],
    primaryFlavors: ['cream', 'cedar', 'almonds'],
    timeCommitment: 40,
    priceRange: 'moderate',
    pairings: ['cappuccino', 'chardonnay', 'ginger ale'],
    description: 'The benchmark mild cigar. Consistently smooth and approachable.',
    idealMoment: 'Daytime smoke, perfect for beginners',
  },
  {
    id: 'montecristo-white',
    brand: 'Montecristo',
    line: 'White Series',
    vitola: 'Rothschild',
    wrapper: 'Ecuadorian Connecticut',
    binder: 'Dominican',
    filler: ['Dominican', 'Nicaraguan'],
    origin: 'Dominican Republic',
    strength: 'mild-medium',
    body: 'medium',
    flavorNotes: ['vanilla', 'cream', 'toasted nuts', 'light pepper'],
    primaryFlavors: ['vanilla', 'cream', 'nuts'],
    timeCommitment: 35,
    priceRange: 'moderate',
    pairings: ['vanilla latte', 'light bourbon', 'cream soda'],
    description: 'Elegant and refined. The white label delivers creamy sophistication.',
    idealMoment: 'Celebratory moments or quiet evenings',
  },
  {
    id: 'davidoff-signature',
    brand: 'Davidoff',
    line: 'Signature Series',
    vitola: '2000',
    wrapper: 'Ecuadorian Connecticut',
    binder: 'Dominican',
    filler: ['Dominican'],
    origin: 'Dominican Republic',
    strength: 'mild',
    body: 'light',
    flavorNotes: ['cream', 'white pepper', 'almonds', 'hay', 'subtle sweetness'],
    primaryFlavors: ['cream', 'white pepper', 'almonds'],
    timeCommitment: 50,
    priceRange: 'luxury',
    pairings: ['champagne', 'earl grey tea', 'fino sherry'],
    description: 'Swiss precision meets Dominican tobacco. The epitome of refinement.',
    idealMoment: 'Special occasions, celebrations',
  },

  // MILD-MEDIUM CIGARS
  {
    id: 'romeo-y-julieta-1875',
    brand: 'Romeo y Julieta',
    line: '1875',
    vitola: 'Bully',
    wrapper: 'Connecticut Shade',
    binder: 'Indonesian',
    filler: ['Dominican', 'Brazilian'],
    origin: 'Dominican Republic',
    strength: 'mild-medium',
    body: 'medium',
    flavorNotes: ['cedar', 'leather', 'cream', 'cocoa', 'subtle spice'],
    primaryFlavors: ['cedar', 'leather', 'cream'],
    timeCommitment: 45,
    priceRange: 'budget',
    pairings: ['americano coffee', 'añejo rum', 'root beer'],
    description: 'Timeless blend that bridges mild and medium. Excellent value.',
    idealMoment: 'Everyday enjoyment',
  },
  {
    id: 'ashton-classic',
    brand: 'Ashton',
    line: 'Classic',
    vitola: 'Magnum',
    wrapper: 'Connecticut Shade',
    binder: 'Dominican',
    filler: ['Dominican'],
    origin: 'Dominican Republic',
    strength: 'mild-medium',
    body: 'medium',
    flavorNotes: ['nutmeg', 'cream', 'cedar', 'light pepper', 'honey'],
    primaryFlavors: ['nutmeg', 'cream', 'cedar'],
    timeCommitment: 55,
    priceRange: 'premium',
    pairings: ['cognac', 'espresso', 'aged rum'],
    description: 'Aged 4+ years for supreme smoothness. A gentleman\'s cigar.',
    idealMoment: 'After dinner, contemplative moments',
  },
  {
    id: 'perdomo-champagne',
    brand: 'Perdomo',
    line: 'Champagne',
    vitola: 'Churchill',
    wrapper: 'Ecuadorian Connecticut',
    binder: 'Nicaraguan',
    filler: ['Nicaraguan'],
    origin: 'Nicaragua',
    strength: 'mild-medium',
    body: 'medium',
    flavorNotes: ['cream', 'cedar', 'almonds', 'subtle citrus', 'toast'],
    primaryFlavors: ['cream', 'cedar', 'almonds'],
    timeCommitment: 75,
    priceRange: 'moderate',
    pairings: ['champagne', 'moscato', 'cream coffee'],
    description: 'Barrel-aged with champagne. Unique flavor journey.',
    idealMoment: 'Celebrations, special evenings',
  },
  {
    id: 'oliva-connecticut',
    brand: 'Oliva',
    line: 'Connecticut Reserve',
    vitola: 'Robusto',
    wrapper: 'Ecuadorian Connecticut',
    binder: 'Nicaraguan',
    filler: ['Nicaraguan'],
    origin: 'Nicaragua',
    strength: 'mild-medium',
    body: 'medium',
    flavorNotes: ['cream', 'toast', 'vanilla', 'light pepper', 'grass'],
    primaryFlavors: ['cream', 'toast', 'vanilla'],
    timeCommitment: 50,
    priceRange: 'budget',
    pairings: ['café con leche', 'prosecco', 'ginger beer'],
    description: 'Nicaraguan tobacco with a Connecticut twist. Great value.',
    idealMoment: 'Afternoon break, casual gatherings',
  },

  // MEDIUM CIGARS
  {
    id: 'padron-2000',
    brand: 'Padrón',
    line: '2000 Series',
    vitola: 'Natural',
    wrapper: 'Nicaraguan',
    binder: 'Nicaraguan',
    filler: ['Nicaraguan'],
    origin: 'Nicaragua',
    strength: 'medium',
    body: 'medium',
    flavorNotes: ['cocoa', 'coffee', 'earth', 'nuts', 'subtle pepper'],
    primaryFlavors: ['cocoa', 'coffee', 'earth'],
    timeCommitment: 45,
    priceRange: 'moderate',
    pairings: ['black coffee', 'añejo tequila', 'dark beer'],
    description: 'The benchmark Nicaraguan puro. Consistent excellence.',
    idealMoment: 'Any time you want reliability',
  },
  {
    id: 'arturo-fuente-hemingway',
    brand: 'Arturo Fuente',
    line: 'Hemingway',
    vitola: 'Short Story',
    wrapper: 'Cameroon',
    binder: 'Dominican',
    filler: ['Dominican'],
    origin: 'Dominican Republic',
    strength: 'medium',
    body: 'medium',
    flavorNotes: ['cedar', 'leather', 'cocoa', 'pepper', 'earth'],
    primaryFlavors: ['cedar', 'leather', 'cocoa'],
    timeCommitment: 30,
    priceRange: 'premium',
    pairings: ['espresso', 'port wine', 'aged rum'],
    description: 'Perfecta shape, perfect smoke. Literary in its complexity.',
    idealMoment: 'Quick but memorable smoke',
  },
  {
    id: 'my-father-flor-antillas',
    brand: 'My Father',
    line: 'Flor de las Antillas',
    vitola: 'Toro',
    wrapper: 'Nicaraguan Sun Grown',
    binder: 'Nicaraguan',
    filler: ['Nicaraguan'],
    origin: 'Nicaragua',
    strength: 'medium',
    body: 'medium',
    flavorNotes: ['baking spices', 'cocoa', 'coffee', 'leather', 'cedar'],
    primaryFlavors: ['baking spices', 'cocoa', 'coffee'],
    timeCommitment: 60,
    priceRange: 'moderate',
    pairings: ['café cubano', 'bourbon', 'dark chocolate'],
    description: '2012 Cigar of the Year. Complex and approachable.',
    idealMoment: 'Evening relaxation, social gatherings',
  },
  {
    id: 'rocky-patel-vintage-1990',
    brand: 'Rocky Patel',
    line: 'Vintage 1990',
    vitola: 'Robusto',
    wrapper: 'Honduran Broadleaf',
    binder: 'Nicaraguan',
    filler: ['Dominican', 'Nicaraguan'],
    origin: 'Honduras',
    strength: 'medium',
    body: 'medium',
    flavorNotes: ['cocoa', 'coffee', 'toast', 'earth', 'subtle sweetness'],
    primaryFlavors: ['cocoa', 'coffee', 'toast'],
    timeCommitment: 55,
    priceRange: 'moderate',
    pairings: ['mocha', 'Irish whiskey', 'cream stout'],
    description: 'Aged broadleaf wrapper delivers chocolate richness.',
    idealMoment: 'Cool evenings, after meals',
  },
  {
    id: 'alec-bradley-prensado',
    brand: 'Alec Bradley',
    line: 'Prensado',
    vitola: 'Robusto',
    wrapper: 'Honduran Corojo',
    binder: 'Honduran',
    filler: ['Honduran', 'Nicaraguan'],
    origin: 'Honduras',
    strength: 'medium',
    body: 'full',
    flavorNotes: ['dark chocolate', 'espresso', 'oak', 'pepper', 'leather'],
    primaryFlavors: ['dark chocolate', 'espresso', 'oak'],
    timeCommitment: 50,
    priceRange: 'moderate',
    pairings: ['ristretto', 'cognac', 'red wine'],
    description: 'Box-pressed perfection. 2011 Cigar of the Year.',
    idealMoment: 'After dinner, contemplation',
  },
  {
    id: 'san-cristobal-elegancia',
    brand: 'San Cristobal',
    line: 'Elegancia',
    vitola: 'Churchill',
    wrapper: 'Ecuadorian Connecticut',
    binder: 'Nicaraguan',
    filler: ['Nicaraguan'],
    origin: 'Nicaragua',
    strength: 'medium',
    body: 'medium',
    flavorNotes: ['cedar', 'cream', 'citrus', 'white pepper', 'toast'],
    primaryFlavors: ['cedar', 'cream', 'citrus'],
    timeCommitment: 70,
    priceRange: 'premium',
    pairings: ['champagne', 'white wine', 'sparkling water with lime'],
    description: 'Elegance defined. Sophisticated and refined.',
    idealMoment: 'Celebrations, refined gatherings',
  },
  {
    id: 'la-aroma-de-cuba-mi-amor',
    brand: 'La Aroma de Cuba',
    line: 'Mi Amor',
    vitola: 'Belicoso',
    wrapper: 'Mexican San Andrés',
    binder: 'Nicaraguan',
    filler: ['Nicaraguan'],
    origin: 'Nicaragua',
    strength: 'medium',
    body: 'full',
    flavorNotes: ['dark chocolate', 'espresso', 'leather', 'earth', 'sweet spice'],
    primaryFlavors: ['dark chocolate', 'espresso', 'leather'],
    timeCommitment: 55,
    priceRange: 'moderate',
    pairings: ['espresso', 'port', 'dark rum'],
    description: 'The "love" line delivers passionate flavor.',
    idealMoment: 'Romantic evenings, deep conversations',
  },

  // MEDIUM-FULL CIGARS
  {
    id: 'padron-1964-anniversary',
    brand: 'Padrón',
    line: '1964 Anniversary Series',
    vitola: 'Exclusivo Maduro',
    wrapper: 'Nicaraguan Maduro',
    binder: 'Nicaraguan',
    filler: ['Nicaraguan'],
    origin: 'Nicaragua',
    strength: 'medium-full',
    body: 'full',
    flavorNotes: ['dark chocolate', 'espresso', 'leather', 'earth', 'black pepper'],
    primaryFlavors: ['dark chocolate', 'espresso', 'leather'],
    timeCommitment: 50,
    priceRange: 'premium',
    pairings: ['double espresso', 'añejo tequila', 'single malt scotch'],
    description: 'The gold standard of Nicaraguan cigars. 4+ years aged.',
    idealMoment: 'Special occasions, quiet reflection',
    rating: 95,
  },
  {
    id: 'arturo-fuente-opus-x',
    brand: 'Arturo Fuente',
    line: 'OpusX',
    vitola: 'Perfecxion No. 5',
    wrapper: 'Dominican Rosado',
    binder: 'Dominican',
    filler: ['Dominican'],
    origin: 'Dominican Republic',
    strength: 'medium-full',
    body: 'full',
    flavorNotes: ['cedar', 'leather', 'pepper', 'cocoa', 'earth', 'sweetness'],
    primaryFlavors: ['cedar', 'leather', 'pepper'],
    timeCommitment: 45,
    priceRange: 'luxury',
    pairings: ['VSOP cognac', 'aged rum', 'strong espresso'],
    description: 'The holy grail. First successful Dominican sun-grown wrapper.',
    idealMoment: 'Milestone celebrations, bucket list smoke',
    rating: 97,
  },
  {
    id: 'oliva-serie-v',
    brand: 'Oliva',
    line: 'Serie V',
    vitola: 'Melanio Robusto',
    wrapper: 'Ecuadorian Sumatra',
    binder: 'Nicaraguan',
    filler: ['Nicaraguan'],
    origin: 'Nicaragua',
    strength: 'medium-full',
    body: 'full',
    flavorNotes: ['dark chocolate', 'espresso', 'cedar', 'pepper', 'earth'],
    primaryFlavors: ['dark chocolate', 'espresso', 'cedar'],
    timeCommitment: 55,
    priceRange: 'premium',
    pairings: ['bourbon', 'dark roast coffee', 'port wine'],
    description: '2014 Cigar of the Year. Rich and complex.',
    idealMoment: 'Evening contemplation, serious smoking',
    rating: 94,
  },
  {
    id: 'drew-estate-liga-privada-9',
    brand: 'Drew Estate',
    line: 'Liga Privada No. 9',
    vitola: 'Robusto',
    wrapper: 'Connecticut Broadleaf Oscuro',
    binder: 'Brazilian Mata Fina',
    filler: ['Honduran', 'Nicaraguan'],
    origin: 'Nicaragua',
    strength: 'medium-full',
    body: 'full',
    flavorNotes: ['dark chocolate', 'coffee', 'leather', 'earth', 'sweetness'],
    primaryFlavors: ['dark chocolate', 'coffee', 'leather'],
    timeCommitment: 60,
    priceRange: 'premium',
    pairings: ['espresso', 'bourbon', 'stout beer'],
    description: 'Originally blended for Steve Saka\'s personal use. Now legendary.',
    idealMoment: 'After a great meal, celebration',
    rating: 95,
  },
  {
    id: 'e-p-carrillo-encore',
    brand: 'E.P. Carrillo',
    line: 'Encore',
    vitola: 'Majestic',
    wrapper: 'Ecuadorian Sumatra',
    binder: 'Nicaraguan',
    filler: ['Nicaraguan'],
    origin: 'Dominican Republic',
    strength: 'medium-full',
    body: 'full',
    flavorNotes: ['leather', 'cedar', 'espresso', 'dark fruit', 'pepper'],
    primaryFlavors: ['leather', 'cedar', 'espresso'],
    timeCommitment: 65,
    priceRange: 'premium',
    pairings: ['XO cognac', 'aged bourbon', 'dark rum'],
    description: '2018 Cigar of the Year. Ernesto\'s masterpiece.',
    idealMoment: 'Award-worthy moments',
    rating: 96,
  },
  {
    id: 'tatuaje-tattoo',
    brand: 'Tatuaje',
    line: 'Tattoo',
    vitola: 'Caballero',
    wrapper: 'Ecuadorian',
    binder: 'Nicaraguan',
    filler: ['Nicaraguan'],
    origin: 'Nicaragua',
    strength: 'medium-full',
    body: 'medium',
    flavorNotes: ['cedar', 'pepper', 'cocoa', 'leather', 'earth'],
    primaryFlavors: ['cedar', 'pepper', 'cocoa'],
    timeCommitment: 50,
    priceRange: 'moderate',
    pairings: ['rye whiskey', 'black coffee', 'craft beer'],
    description: 'Pete Johnson\'s everyday smoke. Bold yet balanced.',
    idealMoment: 'When you want character without commitment',
  },
  {
    id: 'joya-de-nicaragua-antano',
    brand: 'Joya de Nicaragua',
    line: 'Antaño 1970',
    vitola: 'Gran Consul',
    wrapper: 'Nicaraguan Criollo',
    binder: 'Nicaraguan',
    filler: ['Nicaraguan'],
    origin: 'Nicaragua',
    strength: 'medium-full',
    body: 'full',
    flavorNotes: ['leather', 'earth', 'pepper', 'cocoa', 'dried fruit'],
    primaryFlavors: ['leather', 'earth', 'pepper'],
    timeCommitment: 55,
    priceRange: 'moderate',
    pairings: ['scotch', 'mezcal', 'dark beer'],
    description: 'Throwback to Nicaragua\'s bold 1970s blends.',
    idealMoment: 'When you want something primal',
  },

  // FULL STRENGTH CIGARS
  {
    id: 'liga-privada-t52',
    brand: 'Drew Estate',
    line: 'Liga Privada T52',
    vitola: 'Toro',
    wrapper: 'Connecticut Stalk Cut Habano',
    binder: 'Brazilian Mata Fina',
    filler: ['Honduran', 'Nicaraguan'],
    origin: 'Nicaragua',
    strength: 'full',
    body: 'full',
    flavorNotes: ['leather', 'earth', 'dark chocolate', 'pepper', 'cedar'],
    primaryFlavors: ['leather', 'earth', 'dark chocolate'],
    timeCommitment: 70,
    priceRange: 'premium',
    pairings: ['cask strength bourbon', 'espresso', 'peaty scotch'],
    description: 'Bolder than No. 9. For experienced palates.',
    idealMoment: 'Serious smoking sessions',
    rating: 94,
  },
  {
    id: 'padron-family-reserve',
    brand: 'Padrón',
    line: 'Family Reserve',
    vitola: 'No. 45 Maduro',
    wrapper: 'Nicaraguan Maduro',
    binder: 'Nicaraguan',
    filler: ['Nicaraguan'],
    origin: 'Nicaragua',
    strength: 'full',
    body: 'full',
    flavorNotes: ['dark chocolate', 'espresso', 'earth', 'leather', 'black pepper'],
    primaryFlavors: ['dark chocolate', 'espresso', 'earth'],
    timeCommitment: 65,
    priceRange: 'luxury',
    pairings: ['single malt scotch', 'XO cognac', 'espresso romano'],
    description: '10+ years aged. The ultimate Padrón experience.',
    idealMoment: 'Once-in-a-lifetime moments',
    rating: 98,
  },
  {
    id: 'davidoff-yamasa',
    brand: 'Davidoff',
    line: 'Yamasá',
    vitola: 'Robusto',
    wrapper: 'Dominican Yamasá',
    binder: 'Dominican',
    filler: ['Dominican', 'Nicaraguan'],
    origin: 'Dominican Republic',
    strength: 'full',
    body: 'full',
    flavorNotes: ['coffee', 'leather', 'dark chocolate', 'earth', 'red pepper'],
    primaryFlavors: ['coffee', 'leather', 'dark chocolate'],
    timeCommitment: 55,
    priceRange: 'luxury',
    pairings: ['aged rum', 'bourbon', 'Turkish coffee'],
    description: 'Davidoff\'s boldest expression. From the Yamasá Valley.',
    idealMoment: 'When you want Davidoff with muscle',
    rating: 93,
  },
  {
    id: 'plasencia-alma-fuerte',
    brand: 'Plasencia',
    line: 'Alma Fuerte',
    vitola: 'Nestor IV',
    wrapper: 'Honduran Corojo',
    binder: 'Honduran',
    filler: ['Nicaraguan', 'Honduran'],
    origin: 'Honduras',
    strength: 'full',
    body: 'full',
    flavorNotes: ['espresso', 'dark chocolate', 'leather', 'pepper', 'earth'],
    primaryFlavors: ['espresso', 'dark chocolate', 'leather'],
    timeCommitment: 50,
    priceRange: 'premium',
    pairings: ['añejo tequila', 'dark roast coffee', 'islay scotch'],
    description: 'Box-pressed power from five generations of Plasencias.',
    idealMoment: 'Bold moments, bold conversations',
    rating: 93,
  },
  {
    id: 'crowned-heads-jericho-hill',
    brand: 'Crowned Heads',
    line: 'Jericho Hill',
    vitola: '.44S',
    wrapper: 'Mexican San Andrés',
    binder: 'Nicaraguan',
    filler: ['Nicaraguan'],
    origin: 'Nicaragua',
    strength: 'full',
    body: 'full',
    flavorNotes: ['leather', 'earth', 'dark chocolate', 'black pepper', 'oak'],
    primaryFlavors: ['leather', 'earth', 'dark chocolate'],
    timeCommitment: 40,
    priceRange: 'moderate',
    pairings: ['rye whiskey', 'dark beer', 'cold brew'],
    description: 'Named after the Johnny Cash song. Rebellious and bold.',
    idealMoment: 'When you\'re in a Johnny Cash mood',
  },
  {
    id: 'tatuaje-monster-series',
    brand: 'Tatuaje',
    line: 'Monster Series (The Michael)',
    vitola: 'Churchill',
    wrapper: 'Ecuadorian Habano',
    binder: 'Nicaraguan',
    filler: ['Nicaraguan'],
    origin: 'Nicaragua',
    strength: 'full',
    body: 'full',
    flavorNotes: ['leather', 'pepper', 'earth', 'cocoa', 'cedar'],
    primaryFlavors: ['leather', 'pepper', 'earth'],
    timeCommitment: 75,
    priceRange: 'premium',
    pairings: ['peaty scotch', 'dark rum', 'black coffee'],
    description: 'Halloween series legend. Complex and mysterious.',
    idealMoment: 'Halloween season, dark evenings',
    rating: 92,
  },

  // ADDITIONAL MEDIUM CIGARS
  {
    id: 'brick-house-maduro',
    brand: 'Brick House',
    line: 'Maduro',
    vitola: 'Robusto',
    wrapper: 'Brazilian Maduro',
    binder: 'Nicaraguan',
    filler: ['Nicaraguan'],
    origin: 'Nicaragua',
    strength: 'medium',
    body: 'medium',
    flavorNotes: ['chocolate', 'coffee', 'leather', 'earth', 'cream'],
    primaryFlavors: ['chocolate', 'coffee', 'leather'],
    timeCommitment: 50,
    priceRange: 'budget',
    pairings: ['mocha', 'dark beer', 'bourbon'],
    description: 'J.C. Newman\'s value powerhouse. Punches above its weight.',
    idealMoment: 'Everyday enjoyment without breaking the bank',
  },
  {
    id: 'don-carlos-eye-shark',
    brand: 'Arturo Fuente',
    line: 'Don Carlos Eye of the Shark',
    vitola: 'Eye of the Shark',
    wrapper: 'Cameroon',
    binder: 'Dominican',
    filler: ['Dominican'],
    origin: 'Dominican Republic',
    strength: 'medium',
    body: 'medium',
    flavorNotes: ['cedar', 'cream', 'leather', 'cocoa', 'subtle spice'],
    primaryFlavors: ['cedar', 'cream', 'leather'],
    timeCommitment: 55,
    priceRange: 'premium',
    pairings: ['cognac', 'espresso', 'aged rum'],
    description: 'Unique torpedo shape. Elegance personified.',
    idealMoment: 'When you want something special',
    rating: 94,
  },
  {
    id: 'aging-room-quattro-nicaragua',
    brand: 'Aging Room',
    line: 'Quattro Nicaragua',
    vitola: 'Espressivo',
    wrapper: 'Nicaraguan',
    binder: 'Nicaraguan',
    filler: ['Nicaraguan'],
    origin: 'Nicaragua',
    strength: 'medium',
    body: 'medium',
    flavorNotes: ['cedar', 'cocoa', 'coffee', 'nuts', 'subtle pepper'],
    primaryFlavors: ['cedar', 'cocoa', 'coffee'],
    timeCommitment: 45,
    priceRange: 'moderate',
    pairings: ['espresso', 'bourbon', 'dark chocolate'],
    description: 'Boutique quality at accessible prices.',
    idealMoment: 'Discovery smoke, trying something new',
  },
  {
    id: 'new-world-cameroon',
    brand: 'A.J. Fernandez',
    line: 'New World Cameroon',
    vitola: 'Doble Robusto',
    wrapper: 'African Cameroon',
    binder: 'Nicaraguan',
    filler: ['Nicaraguan'],
    origin: 'Nicaragua',
    strength: 'medium',
    body: 'medium',
    flavorNotes: ['cedar', 'cream', 'nuts', 'light pepper', 'toast'],
    primaryFlavors: ['cedar', 'cream', 'nuts'],
    timeCommitment: 65,
    priceRange: 'budget',
    pairings: ['coffee', 'light rum', 'cream soda'],
    description: 'A.J. Fernandez\'s take on the classic Cameroon wrapper.',
    idealMoment: 'Long afternoon smoke',
  },
  {
    id: 'avo-classic',
    brand: 'AVO',
    line: 'Classic',
    vitola: 'No. 2',
    wrapper: 'Ecuadorian Connecticut',
    binder: 'Dominican',
    filler: ['Dominican'],
    origin: 'Dominican Republic',
    strength: 'medium',
    body: 'medium',
    flavorNotes: ['cream', 'cedar', 'nuts', 'white pepper', 'honey'],
    primaryFlavors: ['cream', 'cedar', 'nuts'],
    timeCommitment: 50,
    priceRange: 'premium',
    pairings: ['chardonnay', 'champagne', 'earl grey'],
    description: 'Created by jazz musician Avo Uvezian. Smooth as jazz.',
    idealMoment: 'Listening to music, relaxed evenings',
  },
  {
    id: 'h-upmann-vintage-cameroon',
    brand: 'H. Upmann',
    line: 'Vintage Cameroon',
    vitola: 'Belicoso',
    wrapper: 'African Cameroon',
    binder: 'Dominican',
    filler: ['Dominican', 'Nicaraguan'],
    origin: 'Dominican Republic',
    strength: 'medium',
    body: 'medium',
    flavorNotes: ['cedar', 'nuts', 'cream', 'subtle spice', 'honey'],
    primaryFlavors: ['cedar', 'nuts', 'cream'],
    timeCommitment: 55,
    priceRange: 'moderate',
    pairings: ['coffee', 'light bourbon', 'apple cider'],
    description: 'Historic brand with toothy Cameroon wrapper.',
    idealMoment: 'Classic smoking experience',
  },

  // ADDITIONAL FULL-BODIED
  {
    id: 'camacho-triple-maduro',
    brand: 'Camacho',
    line: 'Triple Maduro',
    vitola: 'Robusto',
    wrapper: 'Honduran Maduro',
    binder: 'Honduran Maduro',
    filler: ['Honduran Maduro'],
    origin: 'Honduras',
    strength: 'full',
    body: 'full',
    flavorNotes: ['dark chocolate', 'espresso', 'leather', 'earth', 'molasses'],
    primaryFlavors: ['dark chocolate', 'espresso', 'leather'],
    timeCommitment: 55,
    priceRange: 'moderate',
    pairings: ['espresso', 'dark rum', 'stout'],
    description: 'Three maduros in one. Unapologetically bold.',
    idealMoment: 'When mild won\'t do',
  },
  {
    id: 'la-flor-dominicana-double-ligero',
    brand: 'La Flor Dominicana',
    line: 'Double Ligero',
    vitola: 'Chisel',
    wrapper: 'Ecuadorian Habano',
    binder: 'Dominican',
    filler: ['Dominican Ligero'],
    origin: 'Dominican Republic',
    strength: 'full',
    body: 'full',
    flavorNotes: ['pepper', 'leather', 'earth', 'coffee', 'dark chocolate'],
    primaryFlavors: ['pepper', 'leather', 'earth'],
    timeCommitment: 60,
    priceRange: 'moderate',
    pairings: ['mezcal', 'espresso', 'peaty scotch'],
    description: 'Double the ligero, double the power. Not for beginners.',
    idealMoment: 'When you need a strong finish',
  },
  {
    id: 'foundation-el-gueguense',
    brand: 'Foundation',
    line: 'El Güegüense (The Wise Man)',
    vitola: 'Churchill',
    wrapper: 'Ecuadorian Corojo',
    binder: 'Nicaraguan',
    filler: ['Nicaraguan'],
    origin: 'Nicaragua',
    strength: 'medium-full',
    body: 'full',
    flavorNotes: ['leather', 'cedar', 'coffee', 'dark chocolate', 'pepper'],
    primaryFlavors: ['leather', 'cedar', 'coffee'],
    timeCommitment: 75,
    priceRange: 'premium',
    pairings: ['aged rum', 'bourbon', 'strong coffee'],
    description: 'Nick Melillo\'s tribute to Nicaraguan culture.',
    idealMoment: 'Long contemplative sessions',
    rating: 93,
  },
  {
    id: 'davidoff-nicaragua',
    brand: 'Davidoff',
    line: 'Nicaragua',
    vitola: 'Robusto',
    wrapper: 'Nicaraguan Habano',
    binder: 'Nicaraguan',
    filler: ['Nicaraguan'],
    origin: 'Nicaragua',
    strength: 'medium-full',
    body: 'full',
    flavorNotes: ['dark chocolate', 'coffee', 'leather', 'cedar', 'pepper'],
    primaryFlavors: ['dark chocolate', 'coffee', 'leather'],
    timeCommitment: 55,
    priceRange: 'luxury',
    pairings: ['single malt', 'dark rum', 'espresso'],
    description: 'Davidoff goes Nicaraguan. Refined strength.',
    idealMoment: 'When you want luxury with power',
    rating: 92,
  },

  // VALUE PICKS
  {
    id: 'factory-smokes-maduro',
    brand: 'Factory Smokes',
    line: 'Maduro',
    vitola: 'Toro',
    wrapper: 'Connecticut Broadleaf',
    binder: 'Nicaraguan',
    filler: ['Nicaraguan'],
    origin: 'Nicaragua',
    strength: 'medium',
    body: 'medium',
    flavorNotes: ['chocolate', 'coffee', 'earth', 'leather'],
    primaryFlavors: ['chocolate', 'coffee', 'earth'],
    timeCommitment: 50,
    priceRange: 'budget',
    pairings: ['black coffee', 'dark beer', 'cola'],
    description: 'Best value in the industry. Great for yard work.',
    idealMoment: 'Everyday smoking, outdoor activities',
  },
  {
    id: 'diesel-unholy-cocktail',
    brand: 'Diesel',
    line: 'Unholy Cocktail',
    vitola: 'Robusto',
    wrapper: 'Connecticut Broadleaf',
    binder: 'Mexican San Andrés',
    filler: ['Nicaraguan', 'Pennsylvanian'],
    origin: 'Nicaragua',
    strength: 'full',
    body: 'full',
    flavorNotes: ['leather', 'earth', 'pepper', 'coffee', 'dark chocolate'],
    primaryFlavors: ['leather', 'earth', 'pepper'],
    timeCommitment: 55,
    priceRange: 'budget',
    pairings: ['bourbon', 'black coffee', 'dark beer'],
    description: 'A.J. Fernandez budget brawler. Serious smoke, friendly price.',
    idealMoment: 'When you want bold without the bill',
  },
  {
    id: 'punch-signature',
    brand: 'Punch',
    line: 'Signature',
    vitola: 'Robusto',
    wrapper: 'Ecuadorian Sumatra',
    binder: 'Honduran',
    filler: ['Honduran', 'Nicaraguan'],
    origin: 'Honduras',
    strength: 'medium',
    body: 'medium',
    flavorNotes: ['cedar', 'leather', 'cocoa', 'nuts', 'light pepper'],
    primaryFlavors: ['cedar', 'leather', 'cocoa'],
    timeCommitment: 50,
    priceRange: 'budget',
    pairings: ['coffee', 'bourbon', 'amber ale'],
    description: 'Classic Honduran flavor at an everyday price.',
    idealMoment: 'Regular rotation smoke',
  },
];

// ============================================
// ASH WISDOM - Daily Reflections
// ============================================

export const ASH_WISDOM: AshWisdom[] = [
  // RITUAL
  {
    id: 'wisdom-1',
    text: 'The ritual is not about the cigar. The cigar is about the ritual.',
    category: 'ritual',
  },
  {
    id: 'wisdom-2',
    text: 'Light slowly. The first flame sets the tone for the entire experience.',
    category: 'ritual',
  },
  {
    id: 'wisdom-3',
    text: 'A cigar shared is a conversation begun.',
    category: 'ritual',
  },
  {
    id: 'wisdom-4',
    text: 'The ash tells a story. Watch it, but don\'t rush it.',
    category: 'ritual',
  },
  {
    id: 'wisdom-5',
    text: 'There is no wrong time for a good cigar. Only better times.',
    category: 'ritual',
  },

  // PRESENCE
  {
    id: 'wisdom-6',
    text: 'A cigar demands nothing but your presence. That is the gift.',
    category: 'presence',
  },
  {
    id: 'wisdom-7',
    text: 'Slow down. The cigar will not wait, but it will not rush you either.',
    category: 'presence',
  },
  {
    id: 'wisdom-8',
    text: 'Be where you are. The cigar is.',
    category: 'presence',
  },
  {
    id: 'wisdom-9',
    text: 'The phone can wait. This moment cannot.',
    category: 'presence',
  },
  {
    id: 'wisdom-10',
    text: 'An hour with a cigar is an hour given back to yourself.',
    category: 'presence',
  },
  {
    id: 'wisdom-11',
    text: 'Smoke rises. Let your thoughts do the same.',
    category: 'presence',
  },
  {
    id: 'wisdom-12',
    text: 'The world moves fast. Your cigar does not. Choose your pace.',
    category: 'presence',
  },

  // CRAFT
  {
    id: 'wisdom-13',
    text: 'Every cigar is the work of hundreds of hands. Smoke accordingly.',
    category: 'craft',
  },
  {
    id: 'wisdom-14',
    text: 'The wrapper protects and reveals. Like all good things.',
    category: 'craft',
  },
  {
    id: 'wisdom-15',
    text: 'Aged tobacco, like aged wisdom, rewards patience.',
    category: 'craft',
  },
  {
    id: 'wisdom-16',
    text: 'The blend is a conversation between leaves. Listen.',
    category: 'craft',
  },
  {
    id: 'wisdom-17',
    text: 'A torcedor gives a cigar its shape. You give it meaning.',
    category: 'craft',
  },
  {
    id: 'wisdom-18',
    text: 'From seed to smoke—years of craft in your hand.',
    category: 'craft',
  },

  // REFLECTION
  {
    id: 'wisdom-19',
    text: 'The best cigars are smoked in good company. Sometimes that company is yourself.',
    category: 'reflection',
  },
  {
    id: 'wisdom-20',
    text: 'Not every cigar needs to be the best. Some just need to be there.',
    category: 'reflection',
  },
  {
    id: 'wisdom-21',
    text: 'A bad day with a good cigar is still a day worth having.',
    category: 'reflection',
  },
  {
    id: 'wisdom-22',
    text: 'The smoke fades. The memory lingers.',
    category: 'reflection',
  },
  {
    id: 'wisdom-23',
    text: 'Judge a cigar by how it makes you feel, not how it makes you look.',
    category: 'reflection',
  },
  {
    id: 'wisdom-24',
    text: 'Some truths reveal themselves between draws.',
    category: 'reflection',
  },
  {
    id: 'wisdom-25',
    text: 'The end of a cigar is not a loss. It is a completion.',
    category: 'reflection',
  },
  {
    id: 'wisdom-26',
    text: 'You don\'t find time for cigars. You make it.',
    category: 'reflection',
  },

  // CONVERSATION
  {
    id: 'wisdom-27',
    text: 'More deals are made in smoke than in boardrooms.',
    author: 'Unknown',
    category: 'conversation',
  },
  {
    id: 'wisdom-28',
    text: 'A cigar is a conversation piece that speaks for itself.',
    category: 'conversation',
  },
  {
    id: 'wisdom-29',
    text: 'Share your cigar knowledge gently. The leaf teaches humility.',
    category: 'conversation',
  },
  {
    id: 'wisdom-30',
    text: 'The lounge is a temple. Treat fellow smokers as fellow seekers.',
    category: 'conversation',
  },
  {
    id: 'wisdom-31',
    text: 'Never criticize another man\'s cigar. His palate is his own.',
    category: 'conversation',
  },

  // FAMOUS QUOTES
  {
    id: 'wisdom-32',
    text: 'A good cigar is like a beautiful chick with a great body who also knows the American League box scores.',
    author: 'Klinger, M*A*S*H',
    category: 'conversation',
  },
  {
    id: 'wisdom-33',
    text: 'Eating and sleeping are the only activities that should be allowed to interrupt a man\'s enjoyment of his cigar.',
    author: 'Mark Twain',
    category: 'reflection',
  },
  {
    id: 'wisdom-34',
    text: 'A cigar ought not to be smoked solely with the mouth, but with the hand, the eyes, and with the spirit.',
    author: 'Zino Davidoff',
    category: 'ritual',
  },
  {
    id: 'wisdom-35',
    text: 'If I had taken my doctor\'s advice and quit smoking when he advised me to, I wouldn\'t have lived to go to his funeral.',
    author: 'George Burns',
    category: 'reflection',
  },
  {
    id: 'wisdom-36',
    text: 'The cigar is the perfect complement to an elegant lifestyle.',
    author: 'George Sand',
    category: 'reflection',
  },
  {
    id: 'wisdom-37',
    text: 'Sometimes a cigar is just a cigar.',
    author: 'Sigmund Freud',
    category: 'reflection',
  },

  // MORE ORIGINAL WISDOM
  {
    id: 'wisdom-38',
    text: 'The first third introduces. The second third develops. The final third reveals.',
    category: 'craft',
  },
  {
    id: 'wisdom-39',
    text: 'Price does not determine pleasure. Attention does.',
    category: 'reflection',
  },
  {
    id: 'wisdom-40',
    text: 'A humidor is not storage. It is stewardship.',
    category: 'craft',
  },
  {
    id: 'wisdom-41',
    text: 'The cut determines the draw. The draw determines the experience.',
    category: 'ritual',
  },
  {
    id: 'wisdom-42',
    text: 'Retrohale once. Then decide.',
    category: 'ritual',
  },
  {
    id: 'wisdom-43',
    text: 'Rest your cigar. It rests you in return.',
    category: 'presence',
  },
  {
    id: 'wisdom-44',
    text: 'The ring gauge matters less than the moment.',
    category: 'reflection',
  },
  {
    id: 'wisdom-45',
    text: 'There are no shortcuts to a good smoke.',
    category: 'craft',
  },
];

// ============================================
// PAIRINGS DATABASE
// ============================================

export const FEATURED_PAIRINGS: Pairing[] = [
  {
    id: 'pairing-1',
    cigarId: 'padron-1964-anniversary',
    drink: 'Woodford Reserve Double Oaked',
    drinkType: 'bourbon',
    description: 'The chocolate notes of the Padrón meet the caramel and vanilla of double-oaked bourbon.',
    whyItWorks: 'Both are aged for complexity. The bourbon\'s sweetness balances the cigar\'s earthiness.',
  },
  {
    id: 'pairing-2',
    cigarId: 'arturo-fuente-hemingway',
    drink: 'Rémy Martin VSOP',
    drinkType: 'cognac',
    description: 'Cameroon wrapper elegance meets French sophistication.',
    whyItWorks: 'The cognac\'s dried fruit notes complement the cigar\'s cedar and leather.',
  },
  {
    id: 'pairing-3',
    cigarId: 'my-father-flor-antillas',
    drink: 'Café Cubano',
    drinkType: 'coffee',
    description: 'Cuban-style espresso with a Nicaraguan puro. Heritage meets heritage.',
    whyItWorks: 'The sweet, strong coffee amplifies the cigar\'s baking spice notes.',
  },
  {
    id: 'pairing-4',
    cigarId: 'davidoff-signature',
    drink: 'Veuve Clicquot Brut',
    drinkType: 'wine',
    description: 'Swiss precision meets French effervescence.',
    whyItWorks: 'The champagne\'s acidity cuts through the cigar\'s creaminess.',
  },
  {
    id: 'pairing-5',
    cigarId: 'oliva-serie-v',
    drink: 'Zacapa 23',
    drinkType: 'rum',
    description: 'Nicaraguan powerhouse meets Guatemalan aged rum.',
    whyItWorks: 'The rum\'s honey and butterscotch notes soften the cigar\'s boldness.',
  },
  {
    id: 'pairing-6',
    cigarId: 'drew-estate-liga-privada-9',
    drink: 'Founder\'s KBS (Kentucky Breakfast Stout)',
    drinkType: 'beer',
    description: 'Bourbon-barrel aged stout with a broadleaf bomb.',
    whyItWorks: 'Both share coffee and chocolate DNA. Made for each other.',
  },
  {
    id: 'pairing-7',
    cigarId: 'macanudo-cafe',
    drink: 'San Pellegrino Limonata',
    drinkType: 'non-alcoholic',
    description: 'Light Italian citrus soda with a mild Connecticut.',
    whyItWorks: 'The subtle sweetness and bubbles cleanse the palate between draws.',
  },
  {
    id: 'pairing-8',
    cigarId: 'padron-family-reserve',
    drink: 'Macallan 18',
    drinkType: 'scotch',
    description: 'Two legends. One moment.',
    whyItWorks: 'Both spent years developing complexity. Both reward patience.',
  },
];

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get Today's Smoke based on current date
 * Uses a deterministic algorithm so all users see the same cigar
 */
export const getTodaysSmoke = (): Cigar => {
  const today = new Date();
  const dayOfYear = Math.floor(
    (today.getTime() - new Date(today.getFullYear(), 0, 0).getTime()) / (1000 * 60 * 60 * 24)
  );
  const index = dayOfYear % CIGARS.length;
  return CIGARS[index];
};

/**
 * Get Today's Wisdom based on current date
 */
export const getTodaysWisdom = (): AshWisdom => {
  const today = new Date();
  const dayOfYear = Math.floor(
    (today.getTime() - new Date(today.getFullYear(), 0, 0).getTime()) / (1000 * 60 * 60 * 24)
  );
  const index = dayOfYear % ASH_WISDOM.length;
  return ASH_WISDOM[index];
};

/**
 * Get Pairing of the Week based on current week
 */
export const getPairingOfTheWeek = (): Pairing & { cigar: Cigar } => {
  const today = new Date();
  const startOfYear = new Date(today.getFullYear(), 0, 1);
  const weekNumber = Math.floor(
    ((today.getTime() - startOfYear.getTime()) / (1000 * 60 * 60 * 24) + startOfYear.getDay() + 1) / 7
  );
  const pairing = FEATURED_PAIRINGS[weekNumber % FEATURED_PAIRINGS.length];
  const cigar = CIGARS.find(c => c.id === pairing.cigarId) || CIGARS[0];
  return { ...pairing, cigar };
};

/**
 * Get cigars by strength
 */
export const getCigarsByStrength = (strength: Cigar['strength']): Cigar[] => {
  return CIGARS.filter(c => c.strength === strength);
};

/**
 * Get cigars by flavor note
 */
export const getCigarsByFlavor = (flavor: string): Cigar[] => {
  const lowerFlavor = flavor.toLowerCase();
  return CIGARS.filter(c =>
    c.flavorNotes.some(f => f.toLowerCase().includes(lowerFlavor)) ||
    c.primaryFlavors.some(f => f.toLowerCase().includes(lowerFlavor))
  );
};

/**
 * Get "If you like X, try Y" recommendations
 */
export const getSimilarCigars = (cigar: Cigar, limit: number = 3): Cigar[] => {
  return CIGARS
    .filter(c => c.id !== cigar.id)
    .map(c => {
      let score = 0;
      // Same strength = high match
      if (c.strength === cigar.strength) score += 3;
      // Adjacent strength = medium match
      const strengths = ['mild', 'mild-medium', 'medium', 'medium-full', 'full'];
      const diff = Math.abs(strengths.indexOf(c.strength) - strengths.indexOf(cigar.strength));
      if (diff === 1) score += 2;
      // Shared primary flavors
      const sharedFlavors = c.primaryFlavors.filter(f => cigar.primaryFlavors.includes(f));
      score += sharedFlavors.length * 2;
      // Same origin
      if (c.origin === cigar.origin) score += 1;
      // Same wrapper type (first word)
      if (c.wrapper.split(' ')[0] === cigar.wrapper.split(' ')[0]) score += 1;
      return { cigar: c, score };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(item => item.cigar);
};

/**
 * Search cigars by query
 */
export const searchCigars = (query: string): Cigar[] => {
  const lowerQuery = query.toLowerCase();
  return CIGARS.filter(c =>
    c.brand.toLowerCase().includes(lowerQuery) ||
    c.line.toLowerCase().includes(lowerQuery) ||
    c.wrapper.toLowerCase().includes(lowerQuery) ||
    c.origin.toLowerCase().includes(lowerQuery) ||
    c.flavorNotes.some(f => f.toLowerCase().includes(lowerQuery))
  );
};
